// V1 settings skeleton — tag + team CRUD; M3 SPEC.md Faz 6 team-management
// ceremony backend ratify.
//
// TeamManagement — Volkan direction 2026-05-23:
//   "seller tarafında da assign to olsun, o da kendi ekibinden (panele eklediği
//    kullanıcılardan) istediği kişiye assign edebilrsin siparişi"
//
// Each surface manages its own team:
//   - admin → PEXIFY Ops Team (assignable in Admin Order Management)
//   - seller → Seller's own staff (assignable in My Orders)
//   - supply → Supply org's production staff (assignable in Work Orders)
//
// Mock-only CRUD against MSW (/settings/team). Backend persistence M3 Stage 4
// SPEC.md Faz 6 — team-management foundational table commit.
// B-6 invariant respected (same human in two orgs = two separate rows);
// T-N17 invariant respected (global email uniqueness gated server-side).

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Mail, Pencil, Plus, Trash2, UserPlus } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { apiUrl } from '../../lib/api'
import { formatRelative } from '../../lib/datetime'
import { useT } from '../../i18n'
import { teamManagementI18n } from '../../i18n/settings/team-management'
import {
  rolesForSurface,
  type TeamMember,
  type TeamMemberStatus,
} from '../../lib/team-roles'
import type { SettingsSurface } from './Settings'
import { DataTableShell } from '../../components/ui/data-table-shell'

type TeamListResponse = {
  data: TeamMember[]
  meta: { total: number; surface: SettingsSurface }
}

type FormState = {
  display_name: string
  email: string
  role: string
  status: TeamMemberStatus
  send_invite: boolean
}

const STATUS_VARIANT: Record<
  TeamMemberStatus,
  'success' | 'warning' | 'default'
> = {
  active: 'success',
  invited: 'warning',
  inactive: 'default',
}

function emptyForm(surface: SettingsSurface): FormState {
  const defaultRole =
    surface === 'admin' ? 'ops' : surface === 'seller' ? 'assistant' : 'helper'
  return {
    display_name: '',
    email: '',
    role: defaultRole,
    status: 'invited',
    send_invite: true,
  }
}

// Map mock-data role descriptors to dictionary-driven bilingual label+summary.
// Mock data exposes EN-only strings (the `value` field is the foreign-key
// identity); the dictionary owns the i18n payload. Stage 4 SPEC.md Faz 1
// ratifies permission semantics — wording layer stays on the FE.
type LocalizedRole = { value: string; label: string; summary: string }

export function TeamManagement({ surface }: { surface: SettingsSurface }) {
  const t = useT(teamManagementI18n)
  const queryClient = useQueryClient()
  const rawRoles = rolesForSurface(surface)
  const roleDict = t.roles[surface] as Record<string, { label: string; summary: string }>
  const roles: LocalizedRole[] = rawRoles.map((r) => ({
    value: r.value,
    label: roleDict[r.value]?.label ?? r.label,
    summary: roleDict[r.value]?.summary ?? r.summary,
  }))
  const headerInfo = t.header[surface]
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<TeamMember | null>(null)
  const [form, setForm] = useState<FormState>(() => emptyForm(surface))
  const [deleteTarget, setDeleteTarget] = useState<TeamMember | null>(null)
  const [error, setError] = useState<string | null>(null)

  const queryKey = ['settings', 'team', surface]
  const { data, isLoading, isError } = useQuery<TeamListResponse>({
    queryKey,
    queryFn: async () => {
      const res = await fetch(apiUrl(`/settings/team?surface=${surface}`))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['settings', 'team'] })

  const saveMutation = useMutation({
    mutationFn: async (payload: { id?: string; body: FormState }) => {
      const method = payload.id ? 'PATCH' : 'POST'
      const url = payload.id
        ? apiUrl(`/settings/team/${payload.id}`)
        : apiUrl('/settings/team')
      const body: Record<string, unknown> = payload.id
        ? {
            display_name: payload.body.display_name,
            role: payload.body.role,
            status: payload.body.status,
          }
        : {
            surface,
            display_name: payload.body.display_name,
            email: payload.body.email,
            role: payload.body.role,
            send_invite: payload.body.send_invite,
          }
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as {
          detail?: string
        }
        throw new Error(errBody.detail ?? `HTTP ${res.status}`)
      }
      return (await res.json()) as TeamMember
    },
    onSuccess: () => {
      refresh()
      setModalOpen(false)
      setEditing(null)
      setError(null)
    },
    onError: (e: Error) => setError(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: async (payload: { id: string; force: boolean }) => {
      const qs = payload.force ? '?force=1' : ''
      const res = await fetch(apiUrl(`/settings/team/${payload.id}${qs}`), {
        method: 'DELETE',
      })
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as {
          detail?: string
          assigned_count?: number
        }
        const err = new Error(errBody.detail ?? `HTTP ${res.status}`) as Error & {
          assigned_count?: number
        }
        err.assigned_count = errBody.assigned_count
        throw err
      }
      return await res.json()
    },
    onSuccess: () => {
      refresh()
      setDeleteTarget(null)
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm(surface))
    setError(null)
    setModalOpen(true)
  }

  const openEdit = (m: TeamMember) => {
    setEditing(m)
    setForm({
      display_name: m.display_name,
      email: m.email,
      role: m.role,
      status: m.status,
      send_invite: false,
    })
    setError(null)
    setModalOpen(true)
  }

  const submitForm = () => {
    if (!editing) {
      if (!form.display_name.trim() || !form.email.trim() || !form.role) {
        setError(t.modal.validation.required)
        return
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        setError(t.modal.validation.invalidEmail)
        return
      }
    }
    saveMutation.mutate({ id: editing?.id, body: form })
  }

  const roleLabel = (value: string) =>
    roles.find((r) => r.value === value)?.label ?? value

  const members = data?.data ?? []

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text)]">
            {headerInfo.title}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">{headerInfo.subtitle}</p>
        </div>
        <Button variant="primary" size="sm" onClick={openCreate}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          {t.addMember}
        </Button>
      </div>

      <div className="mt-5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
        {isLoading && (
          <div className="p-6 text-sm text-[var(--color-text-muted)]">{t.states.loading}</div>
        )}
        {isError && (
          <div className="p-6 text-sm text-rose-700">{t.states.loadFailed}</div>
        )}
        {!isLoading && !isError && members.length === 0 && (
          <div className="p-6 text-sm text-[var(--color-text-muted)]">{t.states.empty}</div>
        )}
        {!isLoading && !isError && members.length > 0 && (
          <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th className="w-12">{t.columns.avatar}</th>
                <th>{t.columns.name}</th>
                <th>{t.columns.email}</th>
                <th>{t.columns.role}</th>
                <th>{t.columns.status}</th>
                <th>{t.columns.lastActive}</th>
                <th className="w-24 text-right">{t.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id}>
                  <td className="py-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-surface-muted)] text-xs font-semibold text-[var(--color-text)]">
                      {m.initials}
                    </span>
                  </td>
                  <td className="py-2 text-[var(--color-text)] font-medium">
                    {m.display_name}
                  </td>
                  <td className="py-2 text-[var(--color-text-muted)]">
                    <span className="inline-flex items-center gap-1 text-xs">
                      <Mail className="h-3 w-3 text-[var(--color-text-muted)]" />
                      {m.email}
                    </span>
                  </td>
                  <td className="py-2">
                    <Badge variant="primary">{roleLabel(m.role)}</Badge>
                  </td>
                  <td className="py-2">
                    <Badge variant={STATUS_VARIANT[m.status]}>
                      {t.status[m.status]}
                    </Badge>
                  </td>
                  <td className="py-2 text-xs text-[var(--color-text-muted)]">
                    {m.status === 'invited' ? (
                      <span className="text-[var(--color-warning)]">
                        {t.invitedPrefix} {m.invited_at ? formatRelative(m.invited_at) : t.noLastActive}
                      </span>
                    ) : (
                      m.last_active_at ? formatRelative(m.last_active_at) : t.noLastActive
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(m)}
                        aria-label={t.ariaLabels.editMember(m.display_name)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {/* Owner/admin protection (audit fix 2026-05-23): cannot
                          remove the org owner — would leave org orphan. M3
                          backend iam-auth re-enforces this rule. */}
                      {m.role !== 'owner' && m.role !== 'admin' ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteTarget(m)}
                          aria-label={t.ariaLabels.removeMember(m.display_name)}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-[var(--color-danger)]" />
                        </Button>
                      ) : (
                        <span
                          className="inline-flex h-9 w-9 items-center justify-center text-xs text-[var(--color-text-muted)]"
                          title={t.ariaLabels.cannotRemove}
                        >
                          —
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </DataTableShell>
        )}
      </div>

      <RolesPreviewMatrix roles={roles} matrixTitle={t.rolesMatrix.title} matrixFooter={t.rolesMatrix.footer} />

      <p className="mt-3 text-xs text-[var(--color-text-muted)]">{t.footerNote}</p>

      {/* Add/Edit modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? t.modal.editTitle : t.modal.addTitle}
            </DialogTitle>
            <DialogDescription>
              {editing ? t.modal.descriptionEdit : t.modal.descriptionAdd}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[var(--color-text)] mb-1">
                {t.modal.displayName}
              </label>
              <input
                type="text"
                value={form.display_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, display_name: e.target.value }))
                }
                className="w-full rounded-md border border-[var(--color-border-strong)] px-3 py-2 text-sm"
                placeholder={t.modal.displayNamePlaceholder}
                maxLength={64}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-[var(--color-text)] mb-1">
                {t.modal.email}
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) =>
                  setForm((f) => ({ ...f, email: e.target.value }))
                }
                className="w-full rounded-md border border-[var(--color-border-strong)] px-3 py-2 text-sm disabled:bg-[var(--color-surface-muted)] disabled:text-[var(--color-text-muted)]"
                placeholder={t.modal.emailPlaceholder}
                disabled={editing !== null}
              />
              {editing && (
                <p className="mt-1 text-xs text-[var(--color-text-muted)]">{t.modal.emailLocked}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-[var(--color-text)] mb-1">
                {t.modal.role}
              </label>
              <select
                value={form.role}
                onChange={(e) =>
                  setForm((f) => ({ ...f, role: e.target.value }))
                }
                className="w-full rounded-md border border-[var(--color-border-strong)] px-3 py-2 text-sm"
              >
                {roles.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                {roles.find((r) => r.value === form.role)?.summary}
              </p>
            </div>

            {editing && (
              <div>
                <label className="block text-xs font-medium text-[var(--color-text)] mb-1">
                  {t.modal.statusLabel}
                </label>
                <select
                  value={form.status}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      status: e.target.value as TeamMemberStatus,
                    }))
                  }
                  className="w-full rounded-md border border-[var(--color-border-strong)] px-3 py-2 text-sm"
                >
                  <option value="active">{t.modal.statusActive}</option>
                  <option value="invited">{t.modal.statusInvited}</option>
                  <option value="inactive">{t.modal.statusInactive}</option>
                </select>
              </div>
            )}

            {!editing && (
              <label className="flex items-center gap-2 text-sm text-[var(--color-text)]">
                <input
                  type="checkbox"
                  checked={form.send_invite}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, send_invite: e.target.checked }))
                  }
                  className="rounded border-[var(--color-border-strong)]"
                />
                <span className="flex items-center gap-1">
                  <UserPlus className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
                  {t.modal.sendInviteLabel}
                </span>
              </label>
            )}

            {error && (
              <div className="rounded-md border-l-4 border-l-rose-400 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
              disabled={saveMutation.isPending}
            >
              {t.modal.cancel}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={submitForm}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending
                ? t.modal.saving
                : editing
                  ? t.modal.update
                  : t.modal.sendInvite}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.deleteDialog.title}</DialogTitle>
            <DialogDescription>
              {t.deleteDialog.leadBefore}
              <strong className="text-[var(--color-text)]">
                {deleteTarget?.display_name}
              </strong>{' '}
              ({deleteTarget?.email}){t.deleteDialog.leadAfter}
            </DialogDescription>
          </DialogHeader>
          {deleteMutation.error && (
            <div className="rounded-md border-l-4 border-l-rose-400 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              {(deleteMutation.error as Error).message}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
            >
              {t.deleteDialog.cancel}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (!deleteTarget) return
                const isRetry = Boolean(deleteMutation.error)
                deleteMutation.mutate({
                  id: deleteTarget.id,
                  force: isRetry,
                })
              }}
            >
              {deleteMutation.isPending
                ? t.deleteDialog.removing
                : deleteMutation.error
                  ? t.deleteDialog.forceConfirm
                  : t.deleteDialog.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function RolesPreviewMatrix({
  roles,
  matrixTitle,
  matrixFooter,
}: {
  roles: LocalizedRole[]
  matrixTitle: string
  matrixFooter: string
}) {
  return (
    <div className="mt-6 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-2">
        {matrixTitle}
      </div>
      <ul className="space-y-1.5 text-xs text-[var(--color-text)]">
        {roles.map((r) => (
          <li key={r.value} className="flex gap-2">
            <span className="inline-flex shrink-0 items-center rounded bg-[var(--color-surface)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text)] ring-1 ring-[var(--color-border)]">
              {r.label}
            </span>
            <span className="text-[var(--color-text-muted)]">{r.summary}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-[var(--color-text-muted)]">{matrixFooter}</p>
    </div>
  )
}
